<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage :title="t.roles.title" :subtitle="t.roles.subtitle" :hasMargin="false" />
            </div>
            <div class="weeduc-admin-page__header-actions" v-if="canManage">
                <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="openCreate">
                    {{ t.roles.new }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar mb-3">
                    <v-row dense align="end">
                        <v-col cols="12" md="5">
                            <v-text-field
                                v-model="search"
                                :label="t.roles.search"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                                class="weeduc-admin-v-field"
                                @update:model-value="debouncedLoad"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="sectorFilter"
                                :items="sectorOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.roles.sector"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                                class="weeduc-admin-v-field"
                                @update:model-value="loadRoles"
                            />
                        </v-col>
                    </v-row>
                </div>

                <Loader v-if="loading" :isActive="false" />
                <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>
                <v-table v-else class="custom-table">
                    <thead>
                        <tr>
                            <th>{{ t.structure.name }}</th>
                            <th>{{ t.roles.sector }}</th>
                            <th class="text-center">{{ t.roles.collaborators }}</th>
                            <th class="text-center">{{ t.roles.courses }}</th>
                            <th class="text-center">{{ t.structure.status }}</th>
                            <th class="text-center">{{ t.common.actions }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!roles.length">
                            <td colspan="6" class="text-center py-6">{{ t.roles.empty }}</td>
                        </tr>
                        <tr v-for="role in roles" :key="role.id">
                            <td>
                                <button
                                    type="button"
                                    class="corporate-roles-name-link"
                                    @click="goRoleDetail(role.id)"
                                >
                                    {{ role.name }}
                                </button>
                                <small class="d-block text-medium-emphasis">{{ role.description }}</small>
                            </td>
                            <td>{{ role.sectorName || '—' }}</td>
                            <td class="text-center">{{ role.collaboratorsCount }}</td>
                            <td class="text-center">{{ (role.courseIds || []).length }}</td>
                            <td class="text-center">
                                {{ role.status === 'active' ? t.structure.active : t.structure.inactive }}
                            </td>
                            <td class="text-center">
                                <v-btn
                                    icon
                                    size="small"
                                    variant="text"
                                    color="primary"
                                    class="me-1"
                                    :title="t.structure.openDetail"
                                    :aria-label="t.structure.openDetail"
                                    @click="goRoleDetail(role.id)"
                                >
                                    <v-icon size="small">mdi-chart-bar</v-icon>
                                </v-btn>
                                <template v-if="canManage">
                                    <v-btn size="small" variant="text" @click="openEdit(role)">{{ t.structure.edit }}</v-btn>
                                    <v-btn size="small" variant="text" @click="duplicateRole(role)">{{ t.roles.duplicate }}</v-btn>
                                    <v-btn size="small" variant="text" color="error" @click="removeRole(role)">
                                        {{ t.structure.delete }}
                                    </v-btn>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </div>
        </div>

        <RoleForm
            v-model="formOpen"
            :title="formTitle"
            :labels="t.roles"
            :structure-labels="t.structure"
            :common="t.common"
            :initial="formInitial"
            :sector-options="sectorOptions.filter((o) => o.value)"
            :course-options="courseOptions"
            :manager-options="managerOptions"
            :saving="saving"
            @submit="saveRole"
        />
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import RoleForm from '@/components/Admin/Corporate/RoleForm.vue';
import rolesApi from '@/utils/corporate/roles';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import usersApi from '@/utils/corporate/users';
import { canManageCorporateStructure } from '@/utils/corporate/permissions';
import { getState } from '@/mocks/corporate/store';
import { isCorporateMockEnabled } from '@/utils/corporate/environment';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';

export default {
    name: 'CorporateRoles',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, RoleForm },
    data() {
        return {
            loading: true,
            error: false,
            saving: false,
            roles: [],
            search: '',
            sectorFilter: null,
            sectorOptions: [],
            courseOptions: [],
            managerOptions: [],
            formOpen: false,
            formInitial: {},
            debounceTimer: null,
        };
    },
    computed: {
        ...mapState(['token']),
        canManage() {
            return canManageCorporateStructure();
        },
        formTitle() {
            return this.formInitial.id ? this.t.roles.formTitleEdit : this.t.roles.formTitleCreate;
        },
    },
    async created() {
        await this.loadFilters();
        await this.loadRoles();
    },
    methods: {
        async loadFilters() {
            const [sectorsRes, managersRes] = await Promise.all([
                organizationalUnitsApi.list(this.token, { type: 'sector' }),
                usersApi.listManagers(this.token),
            ]);
            this.sectorOptions = [
                { text: this.t.common.filterAll, value: null },
                ...(sectorsRes.content || []).map((s) => ({ text: s.name, value: s.id })),
            ];
            this.managerOptions = [
                { text: '—', value: null },
                ...(managersRes.content || []).map((m) => ({ text: m.name, value: m.id })),
            ];
            if (isCorporateMockEnabled()) {
                this.courseOptions = (getState().courses || []).map((c) => ({ text: c.name, value: c.id }));
            } else {
                this.courseOptions = [];
            }
        },
        async loadRoles() {
            this.loading = true;
            this.error = false;
            try {
                const res = await rolesApi.list(this.token, {
                    search: this.search || undefined,
                    sectorId: this.sectorFilter || undefined,
                });
                this.roles = res.content || [];
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        debouncedLoad() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.loadRoles(), 300);
        },
        goRoleDetail(id) {
            if (!id) return;
            this.$router.push(`/dashboard/corporativo/cargos/${id}`);
        },
        openCreate() {
            this.formInitial = {
                name: '',
                description: '',
                sectorId: this.sectorFilter,
                status: 'active',
                courseIds: [],
                managerUserId: null,
            };
            this.formOpen = true;
        },
        openEdit(role) {
            this.formInitial = {
                id: role.id,
                name: role.name,
                description: role.description,
                sectorId: role.sectorId,
                status: role.status,
                courseIds: [...(role.courseIds || [])],
                managerUserId: role.managerUserId,
            };
            this.formOpen = true;
        },
        async saveRole(payload) {
            this.saving = true;
            try {
                if (payload.id) {
                    await rolesApi.update(this.token, payload.id, payload);
                } else {
                    await rolesApi.create(this.token, payload);
                }
                this.formOpen = false;
                await this.loadRoles();
            } catch (e) {
                alert(e.message || this.t.common.error);
            } finally {
                this.saving = false;
            }
        },
        async duplicateRole(role) {
            try {
                await rolesApi.duplicate(this.token, role.id);
                await this.loadRoles();
            } catch (e) {
                alert(e.message || this.t.common.error);
            }
        },
        async removeRole(role) {
            if (!window.confirm(this.t.roles.deleteConfirm)) return;
            try {
                await rolesApi.remove(this.token, role.id);
                await this.loadRoles();
            } catch (e) {
                alert(e.message || this.t.common.error);
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-roles-name-link {
    border: 0;
    background: transparent;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: #250891;
    cursor: pointer;
    text-align: left;
}

.corporate-roles-name-link:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
}

.dashboard.dark .corporate-roles-name-link {
    color: #c4b5fd;
}
</style>
